import Pause from '@mui/icons-material/Pause';
import PlayArrow from '@mui/icons-material/PlayArrow';
import VolumeMute from '@mui/icons-material/VolumeMute';
import VolumeOff from '@mui/icons-material/VolumeOff';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import type Hls from 'hls.js';
import { useState, useRef, useEffect, Children } from 'react';
import { Asset, FocalPointImage as TFocalPointImage, Video } from '@/types/generated';
import AspectRatioPaddingBox from '../AspectRatioPaddingBox';
import { getImageWrapperStyles } from '../Service/ServiceMedia/ServiceMedia.styles';
import YouTubeVideoEmbed from '../YouTubeVideoEmbed';
import { hideControlsForShortVideos, videoHasAudio } from './VideoContent.helpers';
import { richTextVideoControlsWrapperStyles, videoControlIconStyles, videoControlsWrapperStyles, getVideoStyles, youtubeVideoStyles, videoIconButtonStyles, posterImageStyles, videoPlayerContentStyles, videoHoverEffect } from './VideoContent.styles';
import ImageWrapper from '@shared-components/components/ImageWrapper';
import { Maybe } from '@shared-components/types';
import { resolveContentfulAspectRatio } from '@utils/resolveContentfulAspectRatio';
interface IVideoContent {
  posterImage?: Maybe<Asset>;
  focalPointPosterImage?: Maybe<TFocalPointImage>;
  video?: Maybe<Asset>;
  videoElSx?: React.CSSProperties;
  videoControlsSx?: React.CSSProperties;
  controllableVideo?: Maybe<Video>;
  children?: React.ReactNode;
  dataTestId?: string;
  richTextStylePosition?: string;
  loadVideoInPreviewMode?: boolean;
  fromHero?: boolean;
  sixteenByNineVideoRatio?: boolean;
  isNoHeroContent?: boolean;
  mediaAspectRatioSmallScreen?: Maybe<string>;
  overrodeAutoplay?: boolean;
}
function hasSourceChildren(children: React.ReactNode): boolean {
  let hasSourceElements = false;
  Children.forEach(children, c => {
    if (!c) {
      return;
    }
    if ((c as React.ReactElement)?.type === 'source') {
      hasSourceElements = true;
    }
  });
  return hasSourceElements;
}
function getYoutubeAspectRatio(isSmallScreen: boolean, sixteenByNineVideoRatio: boolean): string {
  if (!isSmallScreen) {
    return '16:9';
  }
  return sixteenByNineVideoRatio ? '16:9' : '1:1';
}
function getResolvedVideoSource(hasSourceElements: boolean, videoUrl: string): string | undefined {
  if (hasSourceElements) {
    return undefined;
  }
  return videoUrl;
}
type UseVideoEventBindingsParams = {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isLooped: boolean;
  loadVideoInPreviewMode: boolean;
  setIsVideoReady: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFirstPlayTriggered: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPosterVisual: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAudioControlsVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLongVideo: React.Dispatch<React.SetStateAction<boolean>>;
};
function useVideoEventBindings({
  videoRef,
  isLooped,
  loadVideoInPreviewMode,
  setIsVideoReady,
  setIsPlaying,
  setIsFirstPlayTriggered,
  setIsPosterVisual,
  setIsAudioControlsVisible,
  setIsLongVideo
}: UseVideoEventBindingsParams): void {
  useEffect(() => {
    const currentVideoRef = videoRef.current;
    if (!currentVideoRef) {
      return;
    }
    const loopStartTime = 0;
    const loopEndTime = 3;
    const handleVideoEnd = () => {
      if (!isLooped) {
        currentVideoRef.currentTime = loopStartTime;
      }
    };
    const handleVideoReady = () => {
      setIsVideoReady(true);
    };
    const handleVideoPlaying = () => {
      setIsPlaying(true);
      setIsFirstPlayTriggered(true);
      setIsPosterVisual(false);
      setTimeout(() => {
        setIsAudioControlsVisible(videoHasAudio(videoRef));
      }, 100);
    };
    const handleVideoPaused = () => {
      setIsPlaying(false);
    };
    const handleTimeUpdate = () => {
      if (loadVideoInPreviewMode && currentVideoRef.currentTime >= loopEndTime) {
        currentVideoRef.currentTime = loopStartTime;
        setIsPlaying(true);
      }
    };
    const handleLoadedMetadata = () => {
      setIsLongVideo(hideControlsForShortVideos(videoRef));
      setIsAudioControlsVisible(videoHasAudio(videoRef));
    };
    currentVideoRef.addEventListener('ended', handleVideoEnd);
    currentVideoRef.addEventListener('play', handleVideoPlaying);
    currentVideoRef.addEventListener('pause', handleVideoPaused);
    currentVideoRef.addEventListener('canplaythrough', handleVideoReady);
    currentVideoRef.addEventListener('timeupdate', handleTimeUpdate);
    if ((videoRef.current?.readyState ?? 0) > 1) {
      handleLoadedMetadata();
    } else {
      currentVideoRef.addEventListener('loadedmetadata', handleLoadedMetadata);
    }
    return () => {
      currentVideoRef.removeEventListener('ended', handleVideoEnd);
      currentVideoRef.removeEventListener('play', handleVideoPlaying);
      currentVideoRef.removeEventListener('pause', handleVideoPaused);
      currentVideoRef.removeEventListener('canplaythrough', handleVideoReady);
      currentVideoRef.removeEventListener('timeupdate', handleTimeUpdate);
      currentVideoRef.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [videoRef, isLooped, loadVideoInPreviewMode, setIsVideoReady, setIsPlaying, setIsFirstPlayTriggered, setIsPosterVisual, setIsAudioControlsVisible, setIsLongVideo]);
}
type UseIntersectionPlaybackParams = {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  youtubeId?: Maybe<string>;
  userPaused: boolean;
  computedAutoplay: boolean;
  isFirstPlayTriggered: boolean;
  setIsVideoReady: React.Dispatch<React.SetStateAction<boolean>>;
};
function useIntersectionPlayback({
  videoRef,
  youtubeId,
  userPaused,
  computedAutoplay,
  isFirstPlayTriggered,
  setIsVideoReady
}: UseIntersectionPlaybackParams): void {
  useEffect(() => {
    const currentVideoRef = videoRef.current;
    if (youtubeId?.trim() || !currentVideoRef) {
      return;
    }
    const shouldTriggerIfWeSeeIt = computedAutoplay || !computedAutoplay && isFirstPlayTriggered;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !userPaused && shouldTriggerIfWeSeeIt) {
          void currentVideoRef.play();
          return;
        }
        currentVideoRef.pause();
      });
    }, {
      rootMargin: '0px',
      threshold: 0.5
    });
    observer.observe(currentVideoRef);
    setIsVideoReady(true);
    return () => {
      observer.disconnect();
    };
  }, [videoRef, youtubeId, userPaused, computedAutoplay, isFirstPlayTriggered, setIsVideoReady]);
}
type UseHlsStreamParams = {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  videoSource?: string | null;
  hlsVideoStreamLink?: string | null;
};
function useHlsStream({
  videoRef,
  videoSource,
  hlsVideoStreamLink
}: UseHlsStreamParams): void {
  useEffect(() => {
    if (!videoRef.current || videoSource !== 'HLS Streaming') {
      return;
    }
    let skip = false;
    let hls: Hls | null = null;
    void import('hls.js').then(({
      default: HlsOBJ
    }) => {
      if (skip) {
        return;
      }
      if (videoRef.current && HlsOBJ.isSupported() && hlsVideoStreamLink && videoSource === 'HLS Streaming') {
        hls = new HlsOBJ();
        hls.loadSource(hlsVideoStreamLink);
        hls.attachMedia(videoRef.current);
      }
    });
    return () => {
      skip = true;
      hls?.destroy();
    };
  }, [videoRef, hlsVideoStreamLink, videoSource]);
}
type YoutubeVideoContentProps = {
  youtubeId: string;
  isSmallScreen: boolean;
  sixteenByNineVideoRatio: boolean;
  richTextStylePosition?: string;
  imageWrapperSx: React.CSSProperties;
  posterMedia: Video['posterMedia'];
  computedAutoplay: boolean;
  isLooped: boolean;
  isMuted: boolean;
  fromHero: boolean;
  videoControlsSx: React.CSSProperties;
};
function YoutubeVideoContent({
  youtubeId,
  isSmallScreen,
  sixteenByNineVideoRatio,
  richTextStylePosition,
  imageWrapperSx,
  posterMedia,
  computedAutoplay,
  isLooped,
  isMuted,
  fromHero,
  videoControlsSx
}: Readonly<YoutubeVideoContentProps>) {
  return <AspectRatioPaddingBox aspectRatio={getYoutubeAspectRatio(isSmallScreen, sixteenByNineVideoRatio)} placeholderColor="transparent" imageWrapperSx={imageWrapperSx} paddingBoxSx={youtubeVideoStyles(richTextStylePosition)} data-sentry-element="AspectRatioPaddingBox" data-sentry-component="YoutubeVideoContent" data-sentry-source-file="VideoContent.tsx">
			<YouTubeVideoEmbed youTubeVideoId={youtubeId} isSmallScreen={isSmallScreen} isBorderRadius={false} youTubeVideoImage={posterMedia} autoPlay={computedAutoplay} isLooped={isLooped} isMuted={isMuted} fromHero={fromHero} videoControlsSx={videoControlsSx} sixteenByNineVideoRatio={sixteenByNineVideoRatio} data-sentry-element="YouTubeVideoEmbed" data-sentry-source-file="VideoContent.tsx" />
		</AspectRatioPaddingBox>;
}
function toggleVideoPlayState({
  video,
  isPlaying,
  setUserPaused
}: {
  video: HTMLVideoElement | null;
  isPlaying: boolean;
  setUserPaused: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  if (!video) {
    return;
  }
  if (isPlaying) {
    video.pause();
    setUserPaused(true);
    return;
  }
  void video.play();
  setUserPaused(false);
}
function toggleVideoMutedState({
  video,
  isNowMuted,
  setIsNowMuted
}: {
  video: HTMLVideoElement | null;
  isNowMuted: boolean | null;
  setIsNowMuted: React.Dispatch<React.SetStateAction<boolean | null>>;
}) {
  if (!video) {
    return;
  }
  const nextMuted = !isNowMuted;
  video.muted = nextMuted;
  setIsNowMuted(nextMuted);
}
type VideoControlsProps = {
  controllableVideo: Maybe<Video> | undefined;
  isSmallScreen: boolean;
  isPortraitVideo: boolean;
  videoControlsSx: React.CSSProperties;
  richTextStylePosition?: string;
  isExtraSmallScreen: boolean;
  isHoveredStylesShowed: boolean;
  isPlaying: boolean;
  isAudioControlsVisible: boolean;
  isNowMuted: boolean | null;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  setUserPaused: React.Dispatch<React.SetStateAction<boolean>>;
  setIsNowMuted: React.Dispatch<React.SetStateAction<boolean | null>>;
};
function VideoControls({
  controllableVideo,
  isSmallScreen,
  isPortraitVideo,
  videoControlsSx,
  richTextStylePosition,
  isExtraSmallScreen,
  isHoveredStylesShowed,
  isPlaying,
  isAudioControlsVisible,
  isNowMuted,
  videoRef,
  setUserPaused,
  setIsNowMuted
}: Readonly<VideoControlsProps>) {
  return <Box data-testid={`group_video_controls_for_${controllableVideo?.sys?.id ?? ''}`} sx={[videoControlsWrapperStyles(isSmallScreen, isPortraitVideo), videoControlsSx, richTextStylePosition ? richTextVideoControlsWrapperStyles(isExtraSmallScreen) : {}, videoHoverEffect(isHoveredStylesShowed)]} data-sentry-element="Box" data-sentry-component="VideoControls" data-sentry-source-file="VideoContent.tsx">
			<IconButton sx={videoIconButtonStyles} onClick={() => toggleVideoPlayState({
      video: videoRef.current,
      isPlaying,
      setUserPaused
    })} aria-label={isPlaying ? 'pause' : 'play'} data-sentry-element="IconButton" data-sentry-source-file="VideoContent.tsx">
				{isPlaying ? <Pause data-testid="button_icon_video_pause" sx={videoControlIconStyles} /> : <PlayArrow data-testid="button_icon_video_play" sx={videoControlIconStyles} />}
			</IconButton>
			{isAudioControlsVisible ? <IconButton sx={videoIconButtonStyles} onClick={() => toggleVideoMutedState({
      video: videoRef.current,
      isNowMuted,
      setIsNowMuted
    })} aria-label={isNowMuted ? 'Volume on' : 'Mute'}>
					{isNowMuted ? <VolumeOff data-testid="button_icon_audio_off" sx={videoControlIconStyles} /> : <VolumeMute data-testid="button_icon_audio_on" sx={videoControlIconStyles} />}
				</IconButton> : null}
		</Box>;
}
type NativeVideoContentProps = {
  setIsVideoHovered: React.Dispatch<React.SetStateAction<boolean>>;
  isPosterVisual: boolean;
  computedPosterUrl: string;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  isNowMuted: boolean | null;
  isLooped: boolean;
  computedAutoplay: boolean;
  videoElSx: React.CSSProperties;
  useOriginalVideoSize: boolean;
  isNoHeroContent: boolean;
  fromHero: boolean;
  dataTestId: string;
  hasSourceElements: boolean;
  videoUrl: string;
  setIsVideoReady: React.Dispatch<React.SetStateAction<boolean>>;
  controllableVideo: Maybe<Video> | undefined;
  media: Video['media'];
  isVideoReady: boolean;
  isLongVideo: boolean;
  isSmallScreen: boolean;
  isPortraitVideo: boolean;
  videoControlsSx: React.CSSProperties;
  richTextStylePosition?: string;
  isExtraSmallScreen: boolean;
  isHoveredStylesShowed: boolean;
  isPlaying: boolean;
  isAudioControlsVisible: boolean;
  setUserPaused: React.Dispatch<React.SetStateAction<boolean>>;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  setIsNowMuted: React.Dispatch<React.SetStateAction<boolean | null>>;
};
function NativeVideoContent({
  setIsVideoHovered,
  isPosterVisual,
  computedPosterUrl,
  videoRef,
  isNowMuted,
  isLooped,
  computedAutoplay,
  videoElSx,
  useOriginalVideoSize,
  isNoHeroContent,
  fromHero,
  dataTestId,
  hasSourceElements,
  videoUrl,
  setIsVideoReady,
  controllableVideo,
  media,
  isVideoReady,
  isLongVideo,
  isSmallScreen,
  isPortraitVideo,
  videoControlsSx,
  richTextStylePosition,
  isExtraSmallScreen,
  isHoveredStylesShowed,
  isPlaying,
  isAudioControlsVisible,
  children,
  setUserPaused,
  setIsPlaying: _setIsPlaying,
  setIsNowMuted
}: Readonly<React.PropsWithChildren<NativeVideoContentProps>>) {
  const showControls = isVideoReady && !useOriginalVideoSize && isLongVideo;
  return <Box sx={videoPlayerContentStyles} onMouseOver={() => setIsVideoHovered(true)} onMouseOut={() => setIsVideoHovered(false)} onTouchStart={() => {
    setIsVideoHovered(true);
    setTimeout(() => {
      setIsVideoHovered(false);
    }, 1500);
  }} data-sentry-element="Box" data-sentry-component="NativeVideoContent" data-sentry-source-file="VideoContent.tsx">
			{isPosterVisual && <ImageWrapper src={computedPosterUrl} data-testid="img_video-poster" alt="img_video-poster" sx={posterImageStyles} />}
			<video playsInline ref={node => {
      videoRef.current = node;
    }} controls={false} muted={!!isNowMuted} loop={isLooped} autoPlay={computedAutoplay} style={{
      ...videoElSx,
      ...getVideoStyles(useOriginalVideoSize, isNoHeroContent, fromHero)
    }} disablePictureInPicture data-testid={dataTestId} src={getResolvedVideoSource(hasSourceElements, videoUrl)} onCanPlayThrough={() => setIsVideoReady(true)} id={controllableVideo?.sys?.id ?? ''} aria-label={media?.description ?? ''}>
				{children}
			</video>

			{showControls ? <VideoControls controllableVideo={controllableVideo} isSmallScreen={isSmallScreen} isPortraitVideo={isPortraitVideo} videoControlsSx={videoControlsSx} richTextStylePosition={richTextStylePosition} isExtraSmallScreen={isExtraSmallScreen} isHoveredStylesShowed={isHoveredStylesShowed} isPlaying={isPlaying} isAudioControlsVisible={isAudioControlsVisible} isNowMuted={isNowMuted} videoRef={videoRef} setUserPaused={setUserPaused} setIsNowMuted={setIsNowMuted} /> : null}
		</Box>;
}
export default function VideoContent({
  video,
  controllableVideo,
  posterImage,
  videoElSx = {},
  children,
  dataTestId = 'video_content',
  richTextStylePosition,
  videoControlsSx = {},
  loadVideoInPreviewMode = false,
  fromHero = false,
  sixteenByNineVideoRatio = false,
  isNoHeroContent = false,
  mediaAspectRatioSmallScreen,
  overrodeAutoplay
}: Readonly<IVideoContent>) {
  const {
    autoPlay = false,
    isLooped = true,
    isMuted = true,
    media,
    posterMedia,
    posterUrl,
    youtubeId,
    useOriginalVideoSize = false,
    videoSource,
    hlsVideoStreamLink
  } = new Object(controllableVideo) as Video;
  const computedPosterUrl = posterUrl ?? posterMedia?.url ?? posterImage?.url ?? '';
  const computedAutoplay = Boolean(autoPlay || overrodeAutoplay);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(computedAutoplay);
  const [isFirstPlayTriggered, setIsFirstPlayTriggered] = useState<boolean>(false);
  const [isPosterVisual, setIsPosterVisual] = useState<boolean>(!!computedPosterUrl);
  const [isNowMuted, setIsNowMuted] = useState<boolean | null>(computedAutoplay ? true : isMuted);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isLongVideo, setIsLongVideo] = useState<boolean>(false);
  const [isVideoHovered, setIsVideoHovered] = useState<boolean>(false);
  const [isAudioControlsVisible, setIsAudioControlsVisible] = useState<boolean>(true);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [userPaused, setUserPaused] = useState(false);
  const isPortraitVideo = resolveContentfulAspectRatio(mediaAspectRatioSmallScreen) === '9:16';
  const imageWrapperSx = getImageWrapperStyles(true);
  const videoUrl = media?.url ?? video?.url ?? '';
  useVideoEventBindings({
    videoRef,
    isLooped: Boolean(isLooped),
    loadVideoInPreviewMode,
    setIsVideoReady,
    setIsPlaying,
    setIsFirstPlayTriggered,
    setIsPosterVisual,
    setIsAudioControlsVisible,
    setIsLongVideo
  });
  useIntersectionPlayback({
    videoRef,
    youtubeId,
    userPaused,
    computedAutoplay,
    isFirstPlayTriggered,
    setIsVideoReady
  });
  useHlsStream({
    videoRef,
    videoSource,
    hlsVideoStreamLink
  });
  const hasSourceElements = hasSourceChildren(children);
  const isHoveredStylesShowed = isVideoHovered || !isFirstPlayTriggered && !computedAutoplay || !isPlaying;
  if (videoSource === 'Youtube' || youtubeId?.trim()) {
    if (!youtubeId?.trim()) return null;
    return <YoutubeVideoContent youtubeId={youtubeId} isSmallScreen={isSmallScreen} sixteenByNineVideoRatio={sixteenByNineVideoRatio} richTextStylePosition={richTextStylePosition} imageWrapperSx={imageWrapperSx} posterMedia={posterMedia} computedAutoplay={computedAutoplay} isLooped={Boolean(isLooped)} isMuted={Boolean(isMuted)} fromHero={fromHero} videoControlsSx={videoControlsSx} />;
  }
  return <NativeVideoContent setIsVideoHovered={setIsVideoHovered} isPosterVisual={isPosterVisual} computedPosterUrl={computedPosterUrl} videoRef={videoRef} isNowMuted={isNowMuted} isLooped={Boolean(isLooped)} computedAutoplay={computedAutoplay} videoElSx={videoElSx} useOriginalVideoSize={Boolean(useOriginalVideoSize)} isNoHeroContent={isNoHeroContent} fromHero={fromHero} dataTestId={dataTestId} hasSourceElements={hasSourceElements} videoUrl={videoUrl} setIsVideoReady={setIsVideoReady} controllableVideo={controllableVideo} media={media} isVideoReady={isVideoReady} isLongVideo={isLongVideo} isSmallScreen={isSmallScreen} isPortraitVideo={isPortraitVideo} videoControlsSx={videoControlsSx} richTextStylePosition={richTextStylePosition} isExtraSmallScreen={isExtraSmallScreen} isHoveredStylesShowed={isHoveredStylesShowed} isPlaying={isPlaying} isAudioControlsVisible={isAudioControlsVisible} setUserPaused={setUserPaused} setIsPlaying={setIsPlaying} setIsNowMuted={setIsNowMuted} data-sentry-element="NativeVideoContent" data-sentry-component="VideoContent" data-sentry-source-file="VideoContent.tsx">
			{children}
		</NativeVideoContent>;
}